import type { AttackSkill } from '../../../battle/types';
import type { SkillAnimTiming } from '../../../characters/types';
import { KICK_DAMAGE_MULT } from '../../../data/constants';
import type { CharacterSprite } from '../../../render/CharacterSprite';
import type { Texture } from 'pixi.js';
import type { SkillBundle } from '../../types';

// Blink: a teleport-strike. The attacker blinks (electric flash) BEHIND the
// opponent, mirrored to face their back, and immediately throws a kick. Engine-
// wise it's a kick-strength attack (damageMultiplier = kick mult, launches like
// a kick). The blink sheet is owned by this skill; the kick reuses the
// character's kick sheet.

const ROOT = '/assets/textures/characters/sparky_bot/blink';
const SHEET_COLS = 4;
const SHEET_ROWS = 4;
// Defaults - overridable per character via timings.skills.blink.
const IMPACT_FRAME = 6;
const ANIM_SPEED = 1;

// How far past the defender the attacker lands (design-px), so it stands right
// behind their back rather than on top of them.
const ADJACENT_GAP = 100;

// The kick cuts in this many frames after the arrival snap, while the reappear
// frames are still playing - same idea as the run-in overlap where the swing
// starts on the move.
const KICK_OVERLAP_FRAMES = 2;

const ENGINE: AttackSkill = {
  kind: 'attack',
  id: 'blink',
  name: 'Blink',
  // Hits like a kick.
  damageMultiplier: KICK_DAMAGE_MULT,
  pierceBlock: false,
  animationId: 'blink',
  launches: true,
  // Back attack: no dodge, only absolute blocks (bunker) can stop it.
  fromBehind: true,
};

// Plays the blink flash and snaps `s` to targetX on the burst frame (body
// hidden), then lets the reappear frames finish at the destination. The
// mirror flip happens at the same hidden moment, so the reappear frames
// already face the new direction.
async function blinkTo(
  s: CharacterSprite,
  frames: Texture[],
  targetX: number,
  t: SkillAnimTiming | undefined,
  mirroredOnArrive: boolean,
  // Resolve this many frames after the snap instead of waiting the reappear
  // out - the caller's next animation cuts the rest off.
  earlyOutFrames?: number,
): Promise<void> {
  s.playFrames(frames, {
    loop: false,
    speed: t?.speed ?? ANIM_SPEED,
    offsetX: t?.offsetX,
    offsetY: t?.offsetY,
    scaleMultiplier: t?.scale,
  });
  const impactFrame = t?.impactFrame ?? IMPACT_FRAME;
  await s.awaitFrame(impactFrame);
  if (s.destroyed) return;
  s.x = targetX;
  s.setMirrored(mirroredOnArrive);
  if (earlyOutFrames !== undefined) {
    await s.awaitFrame(impactFrame + earlyOutFrames);
    return;
  }
  await s.awaitAnimationComplete();
}

export const BLINK: SkillBundle = {
  engine: ENGINE,
  assets: {
    sheets: {
      blink: { url: `${ROOT}/blink.png`, cols: SHEET_COLS, rows: SHEET_ROWS },
    },
    textures: {
      logIcon: `${ROOT}/ui-log-icon.png`,
      logCircleIcon: `${ROOT}/ui-log-circle-icon.png`,
    },
  },
  async playAttack(ctx) {
    const { attacker, defender, assets } = ctx;
    const frames = assets.sheets.blink;
    if (!frames) return;
    const t = attacker.timings.skills?.[ENGINE.id];

    // Multi-shot: teleport ONCE, then keep kicking from behind - only the
    // first shot blinks in and only the last one blinks back home.
    const shotIndex = ctx.shotIndex ?? 0;
    const lastShot = shotIndex >= (ctx.shotCount ?? 1) - 1;

    if (shotIndex === 0) {
      // Blink in - land behind the defender, mirrored to face their back. The
      // early out hands over to the kick while the reappear is still playing.
      const targetX = defender.baseX + ADJACENT_GAP * attacker.facing;
      await blinkTo(attacker, frames, targetX, t, true, KICK_OVERLAP_FRAMES);
      if (attacker.destroyed) return;
    }

    // Immediately kick. Await up to its impact frame so the following damage
    // event lands on contact.
    const kt = attacker.timings.kick;
    attacker.playSfx('kick');
    attacker.playAnimation('kick', { loop: false, speed: kt?.speed, scaleMultiplier: kt?.scale });
    await attacker.awaitFrame(kt?.impactFrame ?? 0);

    if (!lastShot) return;

    // Blink back home fire-and-forget so it runs during the upcoming post-damage
    // pause - the damage event stays synced to the kick, not delayed by the exit.
    // The mirror resets at the hidden snap-home moment inside blinkTo.
    void (async () => {
      if (attacker.destroyed) return;
      await blinkTo(attacker, frames, attacker.baseX, t, false);
      if (attacker.destroyed) return;
      attacker.playAnimation('idle', { speed: attacker.timings.idle?.speed });
    })();
  },
};
